import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon, Layers } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useApplyTierToSolutions, useTierImpact } from "@/hooks/use-accounts";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Pass the account with the *new* tierId already set, so the tier-impact
   * query fetches the diff against the correct tier.
   */
  account: { id: string; tierId: string; tierName: string };
}

export function TierChangeSuggestionDialog({ open, onOpenChange, account }: Props) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const tierImpactQuery = useTierImpact(account.id, account.tierId, open);
  const applyMutation = useApplyTierToSolutions(account.id);

  // Stable identity while the query loads — a fresh `?? []` every render would
  // re-fire the reset effect below in an infinite setState loop.
  const affectedSolutions = useMemo(
    () => tierImpactQuery.data?.affectedSolutions ?? [],
    [tierImpactQuery.data],
  );

  // Reset selection when dialog opens / affected list changes
  useEffect(() => {
    if (open) {
      setSelected(new Set(affectedSolutions.map((s) => s.id)));
    }
  }, [open, affectedSolutions]);

  function toggleAll() {
    if (selected.size === affectedSolutions.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(affectedSolutions.map((s) => s.id)));
    }
  }

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function handleApply() {
    const ids = Array.from(selected);
    if (ids.length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      const result = await applyMutation.mutateAsync(ids);
      const count = result.updatedSolutions.length;
      toast.success(
        `Tier updated on ${count} Solution${count !== 1 ? "s" : ""}. Pod Size suggestions are advisory only — no automatic changes were made.`,
      );
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to apply tier to Solutions.");
    }
  }

  const allChecked = affectedSolutions.length > 0 && selected.size === affectedSolutions.length;
  const someChecked = selected.size > 0 && selected.size < affectedSolutions.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Update Solution Tiers</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          The Account Tier has changed to{" "}
          <strong className="text-foreground">{account.tierName}</strong>. The Solutions listed
          below still have a different Tier. Select which to update — this updates their Tier only
          and re-suggests (never auto-applies) Pod Size and weight profiles. No Pod Size will be
          changed automatically.
        </p>

        {tierImpactQuery.isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        )}

        {tierImpactQuery.isError && (
          <ErrorState
            message="Failed to load affected Solutions."
            onRetry={() => tierImpactQuery.refetch()}
          />
        )}

        {!tierImpactQuery.isLoading &&
          !tierImpactQuery.isError &&
          (affectedSolutions.length === 0 ? (
            <EmptyState
              icon={Layers}
              title="All Solutions already match this tier"
              description="No Solutions need updating — they all use the new Account Tier."
            />
          ) : (
            <div className="space-y-1">
              {/* Select all row */}
              <label
                htmlFor="tier-suggest-select-all"
                className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-muted/40"
              >
                <CheckboxPrimitive.Root
                  id="tier-suggest-select-all"
                  checked={allChecked || (someChecked ? "indeterminate" : false)}
                  onCheckedChange={toggleAll}
                  className="flex h-4 w-4 items-center justify-center rounded border border-border bg-background data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                >
                  <CheckboxPrimitive.Indicator>
                    <CheckIcon className="h-3 w-3 text-primary-foreground" />
                  </CheckboxPrimitive.Indicator>
                </CheckboxPrimitive.Root>
                <span className="text-sm font-medium">Select all ({affectedSolutions.length})</span>
              </label>

              <div className="max-h-48 overflow-y-auto rounded-md border border-border">
                {affectedSolutions.map((sol) => (
                  <label
                    key={sol.id}
                    htmlFor={`tier-suggest-sol-${sol.id}`}
                    className="flex cursor-pointer items-center gap-2.5 border-b border-border/50 px-3 py-2.5 last:border-0 hover:bg-muted/40"
                  >
                    <CheckboxPrimitive.Root
                      id={`tier-suggest-sol-${sol.id}`}
                      checked={selected.has(sol.id)}
                      onCheckedChange={() => toggleOne(sol.id)}
                      className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border bg-background data-[state=checked]:border-primary data-[state=checked]:bg-primary"
                    >
                      <CheckboxPrimitive.Indicator>
                        <CheckIcon className="h-3 w-3 text-primary-foreground" />
                      </CheckboxPrimitive.Indicator>
                    </CheckboxPrimitive.Root>
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{sol.name}</span>
                      <span className="text-sm text-muted-foreground">
                        Current tier: {sol.currentTierName}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ))}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applyMutation.isPending}
          >
            Skip
          </Button>
          {affectedSolutions.length > 0 && (
            <Button
              type="button"
              onClick={handleApply}
              disabled={applyMutation.isPending || selected.size === 0}
              aria-busy={applyMutation.isPending}
            >
              {applyMutation.isPending
                ? "Applying…"
                : selected.size === 0
                  ? "Apply to none"
                  : `Apply to ${selected.size} Solution${selected.size !== 1 ? "s" : ""}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
